import { useMemo } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { getAllForms, getFormConfig } from '../forms';
import { ALL_TRADES } from '../utils/constants';

/**
 * Tenant Trade Gating engine (Dev 2 — Sub-Task 2.4)
 *
 * Maps the active tenant's enabled trades (organization.trades) to statutory
 * form categories from the form registry. Enforced in:
 *  - FormLibrary catalog (category tabs + cards)
 *  - Dashboard quick-start generators
 *  - CommandPalette (Cmd+K) search results
 *  - FormRouter (blocks direct URL access to out-of-trade certificates)
 *
 * Tenants with no explicit trades config default to ALL trades so legacy
 * local organizations keep working while PocketBase sync lands.
 */
export const useTenantTrades = () => {
  const { activeTenant } = useTenant();

  const allowedCategories = useMemo(() => {
    const trades = activeTenant?.trades;
    if (!Array.isArray(trades) || trades.length === 0) {
      return [...ALL_TRADES];
    }
    return trades.filter((t) => ALL_TRADES.includes(t));
  }, [activeTenant]);

  const isCategoryAllowed = useMemo(
    () => (category) => allowedCategories.includes(category),
    [allowedCategories]
  );

  const isFormAllowed = useMemo(
    () => (formId) => {
      const config = getFormConfig(formId);
      if (!config) return false;
      return allowedCategories.includes(config.category);
  },
    [allowedCategories]
  );

  const filterFormsForTenant = useMemo(
    () => (forms) => {
      const source = forms || getAllForms();
      return source.filter((form) => allowedCategories.includes(form.category));
    },
    [allowedCategories]
  );

  const allowedForms = useMemo(() => filterFormsForTenant(getAllForms()), [filterFormsForTenant]);

  return {
    /** string[] — trade categories the tenant may access (defaults to all) */
    allowedCategories,
    /** (category) => boolean */
    isCategoryAllowed,
    /** (formId) => boolean — registry lookup + category check */
    isFormAllowed,
    /** (forms?) => forms[] — filter a list (or the whole registry) by tenant trades */
    filterFormsForTenant,
    /** forms[] — the whole registry pre-filtered for the active tenant */
    allowedForms,
    /** true when the tenant record carried explicit trades from PocketBase */
    hasExplicitTrades: Array.isArray(activeTenant?.trades) && activeTenant.trades.length > 0
  };
};

export default useTenantTrades;
